import {
  FocusContext,
  FocusHandler,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import React, { useEffect, useMemo, useState } from 'react'

import { isArrowLeft, isArrowRight } from '.'
import {
  platformTransitionStyle,
  removeDuplicatesByProperty,
} from '../../../../shared/helpers'
import tts from '../../lib/TTS'

import { useNavigationMode } from '../../../../shared/providers/NavigationModeProvider/NavigationModeProvider'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import HorizontalScroll from '../HorizontalScroll/HorizontalScroll'
import type { VirtualizedListProps } from './types'

const DEFAULT_HIDDEN_ITEMS = 2

export function VirtualizedList<T,>({
  data: originalData,
  layout,
  hiddenItems = DEFAULT_HIDDEN_ITEMS,
  ttsText,
  children,
  onFocus,
  handleInfiniteFetching,
  collectionTitle,
}: VirtualizedListProps<T>) {
  const cleanedData = useMemo(() => {
    if (originalData.length > 0) {
      const key = (originalData[0] as any).feed_cid ? 'feed_cid' : 'cid'
      return removeDuplicatesByProperty(originalData, key)
    }
    return originalData
  }, [originalData])
  const dataLength = cleanedData.length

  const {
    ref: scrollingRef,
    focusKey,
    hasFocusedChild,
    isHovered,
  } = useHybridFocusable({ trackChildren: true, onFocus })
  const itemWidth = layout.cardWidth + layout.cardMarginRight
  const containerWidth = itemWidth * layout.screenVisibleItems
  const visibleItems = Math.ceil(containerWidth / itemWidth)
  let maxVisibleItems = visibleItems + hiddenItems
  const [startIndex, setStartIndex] = useState(0)
  const [readCarouselTitle, setReadCarouselTitle] = useState(false)
  const [translate, setTranslate] = useState(0)
  const [lastIndexFocused, setLastIndexFocused] = useState(0)
  const endIndex = Math.min(startIndex + maxVisibleItems, dataLength)
  const invisibleItemsWidthEnd = (dataLength - endIndex) * itemWidth
  const invisibleItemsWidthStart = startIndex * itemWidth
  const scrollableContainerWidth = dataLength * itemWidth
  const { isCursorMode } = useNavigationMode()
  const scrollStride = visibleItems - 1 // overlap by 1: the cut item becomes fully visible
  const currentVisualStart = Math.round(-translate / itemWidth)
  const canScrollRight = currentVisualStart + visibleItems - 1 < dataLength

  const cards = useMemo(
    () => cleanedData.slice(startIndex, endIndex),
    [startIndex, endIndex]
  )

  maxVisibleItems =
    maxVisibleItems - hiddenItems >= dataLength ? dataLength : maxVisibleItems

  useEffect(() => {
    if (hasFocusedChild) {
      setReadCarouselTitle(true)
    } else {
      setReadCarouselTitle(false)
    }
  }, [hasFocusedChild, setReadCarouselTitle])

  useEffect(() => {
    if (readCarouselTitle) {
      tts.pushTextToRead(ttsText || '')
    }
  }, [readCarouselTitle])

  function handleHorizontalScrolling(direction: string) {
    if (direction === 'right') {
      if (canScrollRight) {
        const newVisualStart = Math.min(
          currentVisualStart + scrollStride,
          Math.max(dataLength - visibleItems + 1, 0)
        )
        // Keep startIndex 1 behind visual start as a buffer for handleCardFocus
        const newStartIndex = Math.max(newVisualStart - 1, 0)
        const focusIdx = Math.min(
          newVisualStart + visibleItems - 2,
          dataLength - 1
        )

        setStartIndex(newStartIndex)
        setTranslate(-newVisualStart * itemWidth)
        setFocus(`${collectionTitle}-${focusIdx}`)
        setLastIndexFocused(focusIdx)

        if (typeof handleInfiniteFetching === 'function') {
          handleInfiniteFetching()
        }
      }
    }

    if (direction === 'left') {
      if (currentVisualStart > 0) {
        const newVisualStart = Math.max(currentVisualStart - scrollStride, 0)
        const newStartIndex = Math.max(newVisualStart - 1, 0)
        const focusIdx = newVisualStart

        setStartIndex(newStartIndex)
        setTranslate(-newVisualStart * itemWidth)
        setFocus(`${collectionTitle}-${focusIdx}`)
        setLastIndexFocused(focusIdx)
      }
    }
  }

  function handleCardFocus(
    ...args: Parameters<FocusHandler<{ index: number }>>
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, { index }, details] = args

    if (index !== lastIndexFocused) {
      // @ts-ignore @TODO: spatial-navigation types are not up to date
      if (isArrowRight(details.event)) {
        if (index + 1 >= visibleItems) {
          setTranslate((previous) => previous - itemWidth)
        }
        if (index >= visibleItems) {
          const newStartIndex = Math.min(
            dataLength - maxVisibleItems + 2,
            startIndex + 1
          )
          setStartIndex(newStartIndex)
        }
        if (
          typeof handleInfiniteFetching === 'function' &&
          dataLength - index === 4
        ) {
          handleInfiniteFetching()
        }
      }
      // @ts-ignore @TODO: spatial-navigation types are not up to date
      if (isArrowLeft(details.event)) {
        if (index >= visibleItems - 2) {
          setTranslate((previous) => previous + itemWidth)
        }

        if (index >= visibleItems - 1) {
          const newStartIndex = Math.max(startIndex - 1, 0)
          setStartIndex(newStartIndex)
        }
      }

      setLastIndexFocused(index)
    }
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className="virtualized-list"
        style={{ marginBottom: `${layout.carouselMarginBottom}px` }}
      >
        <div
          className="virtualized-list__container"
          ref={scrollingRef}
          style={{ width: `${scrollableContainerWidth}px` }}
        >
          {isHovered && isCursorMode && (
            <HorizontalScroll
              onHorizontalScrolling={handleHorizontalScrolling}
              heightStyle={`${layout.cardHeight}px`}
              canScrollLeft={Math.abs(translate / itemWidth) >= 1}
              canScrollRight={canScrollRight}
            />
          )}
          <div
            className="virtualized-list__scrollable-container"
            style={{
              width: `${scrollableContainerWidth}px`,
              transform: `translateX(${translate}px)`,
              transition: platformTransitionStyle(
                'transform 500ms ease-in-out'
              ),
            }}
          >
            <div
              className="virtualized-list__invisible-items-start"
              style={{ width: `${invisibleItemsWidthStart}px` }}
            />
            <div
              className="virtualized-list__row-container"
              style={{
                width: `${cards.length * itemWidth + 100}px`,
              }}
            >
              <div className="virtualized-list__row">
                {typeof children === 'function' &&
                  useMemo(
                    () => children({
                        cards,
                        startIndex,
                        onFocus: handleCardFocus,
                        readCarouselTitle,
                      }),
                    [children, translate, readCarouselTitle, startIndex]
                  )}
              </div>
            </div>
            <div
              className="virtualized-list__invisible-items-end"
              style={{ width: `${invisibleItemsWidthEnd}px` }}
            />
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  )
}
